import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

const controlStyles =
  'w-full rounded-[--radius-control] border border-line-strong bg-surface px-3 py-2 text-sm ' +
  'text-ink placeholder:text-ink-muted transition-colors ' +
  'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-ring/50 ' +
  'disabled:bg-surface-sunken disabled:text-ink-muted aria-[invalid=true]:border-bad';

interface FieldShellProps {
  label: string;
  htmlFor: string;
  error?: string | undefined;
  hint?: ReactNode;
  children: ReactNode;
}

function FieldShell({ label, htmlFor, error, hint, children }: FieldShellProps) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-[13px] font-medium text-ink-secondary">
        {label}
      </label>
      {children}
      {error ? (
        <p className="text-[13px] text-bad" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="text-[13px] text-ink-muted">{hint}</p>
      ) : null}
    </div>
  );
}

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  error?: string | undefined;
  hint?: ReactNode;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, hint, className, ...props },
  ref
) {
  const id = useId();
  return (
    <FieldShell label={label} htmlFor={id} error={error} hint={hint}>
      <input
        id={id}
        ref={ref}
        aria-invalid={error ? true : undefined}
        className={cn(controlStyles, className)}
        {...props}
      />
    </FieldShell>
  );
});

export interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string;
  error?: string | undefined;
  hint?: ReactNode;
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { label, error, hint, className, children, ...props },
  ref
) {
  const id = useId();
  return (
    <FieldShell label={label} htmlFor={id} error={error} hint={hint}>
      <select id={id} ref={ref} className={cn(controlStyles, className)} {...props}>
        {children}
      </select>
    </FieldShell>
  );
});
